import * as React from 'react';
import { Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

type LangCode = 'en' | 'ar' | 'fr';

const LANGUAGE_KEYS: Record<LangCode, string> = {
    en: 'common:english',
    ar: 'common:arabic',
    fr: 'common:french',
};

interface LanguageSelectorDropdownProps {
    /** When true, show only the globe icon (for narrow sidebars) */
    compact?: boolean;
    /** Trigger classes, e.g. the CRM top bar's glass chip. */
    className?: string;
}

export default function LanguageSelectorDropdown({
    compact = false,
    className,
}: LanguageSelectorDropdownProps) {
    const { t, i18n } = useTranslation();

    const setLanguage = (lang: LangCode) => {
        i18n.changeLanguage(lang);
    };

    const currentLang = (i18n.language?.split('-')[0] || 'en') as LangCode;
    const effectiveLang: LangCode = ['en', 'ar', 'fr'].includes(currentLang) ? currentLang : 'en';

    const languages: { value: LangCode }[] = [{ value: 'en' }, { value: 'ar' }, { value: 'fr' }];

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size={compact ? 'icon' : 'sm'}
                    className={cn(compact ? 'w-12 h-12' : 'w-fit px-3', className)}
                    aria-label={compact ? t(LANGUAGE_KEYS[effectiveLang]) : undefined}
                    data-screenshot="toggle-language"
                >
                    <Globe className={compact ? 'h-5 w-5' : 'me-2 h-4 w-4'} />
                    {!compact && t(LANGUAGE_KEYS[effectiveLang])}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {languages.map(lang => (
                    <DropdownMenuItem
                        key={lang.value}
                        onClick={() => setLanguage(lang.value)}
                        className={i18n.language?.startsWith(lang.value) ? 'bg-accent' : ''}
                    >
                        {t(LANGUAGE_KEYS[lang.value])}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
